import { CheckCircle2, Zap, Crown } from "lucide-react";
import { useState } from "react";

export default function Plans() {
  const [loading, setLoading] = useState(null);
  const [trialLoading, setTrialLoading] = useState(null);
  const [error, setError] = useState("");

  const handleCheckout = async (plan) => {
    setLoading(plan.name);
    setError("");
    const token = localStorage.getItem("token");

    try {
      const response = await fetch("https://zaichat.net/api/create_checkout_session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          auth: `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan_name: plan.name,
          amount: typeof plan.price === "number" ? plan.price * 100 : 0,
        }),
      });

      const data = await response.json();
      console.log(data)
      if (data.data.url) window.location.href = data.data.url;
      else throw new Error("No checkout URL returned");
    } catch (err) {
      setError("Failed to start checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  const handleFreeTrial = async (plan) => {
    setTrialLoading(plan.name);
    const token = localStorage.getItem("token");

    try {
      const response = await fetch("https://zaichat.net/api/start-free-trial", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          auth: `Bearer ${token}`,
        },
  
      });

      const data = await response.json();

      if (data.success) {
        alert(`3-Day Free Trial Activated for ${plan.name}`);
      } else {
        alert("Failed to activate free trial.");
      }
    } catch (err) {
      alert("Error activating free trial.");
    } finally {
      setTrialLoading(null);
    }
  };

  const plans = [
    {
      name: "Basic",
      price: 600,
      icon: <Zap className="w-8 h-8 text-white" />,
      description: "Perfect for small & growing businesses.",
      gradient: "from-[#00C6AE] to-[#009D84]",
      features: [
        "1 WhatsApp number",
        "6,000 messages/month",
        "Product catalog",
        "Basic CRM",
        "Basic analytics",
        "2 languages",
        "Shopify, WooCommerce, Sheets",
        "5 days history",
        "Email support",
      ],
    },
    {
      name: "Enterprise",
      price: "Custom",
      icon: <Crown className="w-8 h-8 text-white" />,
      description: "For large companies with custom needs.",
      gradient: "from-[#FF8A00] to-[#FF3D00]",
      features: [
        "Unlimited WhatsApp numbers",
        "Unlimited messages",
        "Unlimited automations",
        "Advanced AI",
        "Advanced analytics",
        "Full CRM",
        "Team inbox",
        "Custom onboarding",
        "Dedicated manager",
        "API access",
        "Unlimited languages",
        "24/7 priority support",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#eef2f3] to-[#dfe9f3] py-20 px-6">

      {/* PAGE HEADER */}
      <div className="text-center mb-16">
        <h1 className="text-6xl font-extrabold text-primary drop-shadow-sm">
          Pricing Plans
        </h1>
        <p className="text-dark/60 text-lg mt-3 max-w-xl mx-auto">
          Choose the plan that fits your business growth.
        </p>
      </div>

      {/* ERROR MESSAGE */}
      {error && (
        <div className="max-w-xl mx-auto mb-8 bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg text-center">
          {error}
        </div>
      )}

      {/* PLANS GRID */}
      <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
        {plans.map((plan, idx) => (
          <div
            key={idx}
            className="backdrop-blur-xl bg-white/60 shadow-2xl border border-white/40 rounded-3xl p-10 hover:-translate-y-2 transition-all duration-300"
          >
            {/* ICON */}
            <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${plan.gradient} flex items-center justify-center shadow-xl mb-6`}>
              {plan.icon}
            </div>

            {/* TITLE & DESCRIPTION */}
            <h2 className="text-3xl font-bold text-primary">{plan.name}</h2>
            <p className="text-dark/60 text-sm mt-2 mb-6">{plan.description}</p>

            {/* PRICE */}
            <div className="mb-8">
              <span className="text-5xl font-extrabold text-primary">
                {typeof plan.price === "number" ? plan.price : "Custom"}
              </span>
              {typeof plan.price === "number" && (
                <p className="text-dark/50 text-sm -mt-1">SAR / month</p>
              )}
            </div>

            {/* FEATURES */}
            <ul className="space-y-4 mb-10">
              {plan.features.map((f, i) => (
                <li key={i} className="flex items-center gap-3 text-dark/80">
                  <CheckCircle2 className="text-accent" size={18} />
                  {f}
                </li>
              ))}
            </ul>

            {/* BUTTON */}
            <button
              onClick={() => handleCheckout(plan)}
              disabled={loading === plan.name || plan.price === "Custom"}
              className="w-full py-3 bg-accent text-white rounded-xl font-semibold shadow-lg hover:bg-accent/90 disabled:opacity-50 transition"
            >
              {loading === plan.name
                ? "Processing..."
                : plan.price === "Custom"
                ? "Contact Sales"
                : "Choose Plan"}
            </button>

            {/* FREE TRIAL BUTTON */}
            {plan.price !== "Custom" && (
              <button
                onClick={() => handleFreeTrial(plan)}
                disabled={trialLoading === plan.name}
                className="w-full mt-3 py-3 border-2 border-accent text-accent rounded-xl font-semibold hover:bg-accent hover:text-white transition"
              >
                {trialLoading === plan.name ? "Activating..." : "Start 3-Day Free Trial"}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* FOOTER */}
      <p className="text-center text-dark/60 text-sm mt-14">
        Need a custom enterprise deployment?{" "}
        <a href="mailto:sales@zuccess.com" className="text-accent font-semibold hover:underline">
          Contact our sales team →
        </a>
      </p>
    </div>
  );
}
